import { ReactNode, CSSProperties } from "react";
import {
  TooltipDialog,
  Button,
  type TooltipDialogProps,
} from "@newsela/angelou";

import { useHoverTooltip } from "@/hooks/useHoverTooltip";

export interface HoverTooltipProps
  extends Omit<TooltipDialogProps, "buttonContents" | "body"> {
  renderContent: () => ReactNode;
  children: ReactNode;
  closeDelay?: number;
}

/**
 * A generic tooltip component that extends TooltipDialog from Angelou with hover interaction support.
 *
 * Features:
 * - Opens tooltip on hover over trigger element
 * - Keeps tooltip open when hovering over tooltip content
 * - Configurable close delay
 * - Works with any child component (Link, Button, etc.)
 * - Fully accessible with ARIA attributes
 * - Supports both ReactNode and render prop for children
 *
 * @example
 * ```tsx
 * // With a link (ReactNode)
 * <HoverTooltip
 *   renderContent={() => (
 *     <div>
 *       Tooltip content
 *     </div>
 *   )}
 *   placement="top"
 *   closeDelay={100}
 * >
 *   <Link href="/path">Click me!</Link>
 * </HoverTooltip>
 *
 * // With a render prop
 * <HoverTooltip
 *   renderContent={() => (
 *     <div>
 *       Tooltip content
 *     </div>
 *   )}
 *   header="Information"
 *   closeDelay={200}
 * >
 *   {() => <Button>Hover me!</Button>}
 * </HoverTooltip>
 * ```
 */
export const HoverTooltip = ({
  children,
  renderContent,
  closeDelay = 100,
  onOpen,
  onClose,
  buttonProps,
  ...rest
}: HoverTooltipProps) => {
  const {
    wrapperRef,
    handleTriggerEnter,
    handleTriggerLeave,
    handleTooltipEnter,
    handleTooltipLeave,
    handleTooltipOpen,
    handleTooltipClose,
  } = useHoverTooltip({ closeDelay });

  const tooltipContent = (
    <div onMouseEnter={handleTooltipEnter} onMouseLeave={handleTooltipLeave}>
      {renderContent()}
    </div>
  );

  const wrapperStyle: CSSProperties = {
    display: "inline-block",
  };

  const handleOpen = () => {
    handleTooltipOpen();
    onOpen?.();
  };

  const handleClose = () => {
    handleTooltipClose();
    onClose?.();
  };

  const mergedButtonProps = {
    flavor: Button.flavors.incognito,
    __cssFor: {
      root: {
        pointerEvents: "auto" as const,
      },
      ...buttonProps?.__cssFor,
    },
    ...buttonProps,
  };

  return (
    <span
      onMouseEnter={handleTriggerEnter}
      onMouseLeave={handleTriggerLeave}
      ref={wrapperRef as React.Ref<HTMLSpanElement>}
      style={wrapperStyle}
    >
      <TooltipDialog
        body={tooltipContent}
        buttonContents={children}
        onOpen={handleOpen}
        onClose={handleClose}
        buttonProps={mergedButtonProps}
        {...rest}
      />
    </span>
  );
};

export default HoverTooltip;
